import React, { useEffect, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { Button, IconButton } from '@/modules/ui/components';
import {
  clearChunkReloadCount,
  reloadWithCacheBust,
} from '@/modules/core/chunkLoadRecovery';

const DISMISSED_KEY = 'version_checker_dismissed';
const POLL_MS = 2 * 60 * 1000;

function getScriptSources(doc) {
  return Array.from(doc.getElementsByTagName('script'))
    .map((s) => s.src)
    .filter((src) => src && src.includes('/assets/') && !src.includes('chrome-extension'));
}

/**
 * Polls `/` for new hashed script URLs and prompts the user to reload when a deploy landed.
 */
export default function VersionChecker() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === 'true',
  );

  useEffect(() => {
    if (import.meta.env.DEV) return undefined;

    const initialScripts = JSON.stringify(getScriptSources(document));
    let pageUnloading = false;

    const checkForUpdates = async () => {
      if (pageUnloading || document.visibilityState !== 'visible') return;
      try {
        const response = await fetch(`/?__v=${Date.now()}`, {
          cache: 'no-store',
          headers: { Pragma: 'no-cache', 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const latestScripts = JSON.stringify(getScriptSources(doc));

        if (initialScripts !== latestScripts && latestScripts !== '[]') {
          setHasUpdate(true);
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdates();
    };
    const onPageHide = () => {
      pageUnloading = true;
    };

    void checkForUpdates();
    const intervalId = setInterval(checkForUpdates, POLL_MS);
    const initialTimeout = setTimeout(checkForUpdates, 5_000);
    window.addEventListener('focus', checkForUpdates);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onPageHide);

    let removeSwListener = () => {};
    if ('serviceWorker' in navigator) {
      const onControllerChange = () => {
        void checkForUpdates();
      };
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
      removeSwListener = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      };
      navigator.serviceWorker.ready
        .then((reg) => {
          try {
            void reg.update();
          } catch {
            /* ignore */
          }
        })
        .catch(() => {});
    }

    return () => {
      pageUnloading = true;
      clearInterval(intervalId);
      clearTimeout(initialTimeout);
      window.removeEventListener('focus', checkForUpdates);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onPageHide);
      removeSwListener();
    };
  }, []);

  useEffect(() => {
    if (hasUpdate) {
      const t = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [hasUpdate]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleReload = () => {
    clearChunkReloadCount();
    reloadWithCacheBust();
  };

  if (import.meta.env.DEV || !hasUpdate || dismissed) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-20 left-4 right-4 z-[60] transition-all duration-300 ease-out sm:left-auto sm:right-4 sm:max-w-md ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 text-gray-900 dark:text-white shadow-lg">
        <div className="flex min-w-0 items-center gap-3">
          <FiRefreshCw className="h-5 w-5 shrink-0 text-brand-600 dark:text-brand-400" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold">Update available</p>
            <p className="text-xs text-gray-600 dark:text-white/70">A new version is ready — refresh to get it.</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="small"
            onClick={handleReload}
            className="cursor-pointer"
          >
            Refresh
          </Button>
          <IconButton
            label="Dismiss update notice"
            size="small"
            onClick={handleDismiss}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </IconButton>
        </div>
      </div>
    </div>
  );
}
